// Reading the notes list, and every write this screen makes that is not the
// editor's own autosave.
//
// The split is the one `day/useDayScreen.ts` and `edit/useEditor.ts` already
// keep: a read that fails leaves a screen with no list on it and says so where
// the list would be, while a write that fails has to be said out loud somewhere
// the person is looking. There is no sheet on this screen, so the toast is that
// place - which is why nothing here quietly swallows a failure.
//
// THE SEARCH IS THE SERVER'S. Typing in the box does not filter a list this
// module is holding; it asks again, after a pause. That costs a request and buys
// the thing that matters: one statement of "does this note match", which a
// native client asks the same way. What is held while the next answer is on its
// way is the LAST answer - emptying the list on every keystroke would flash a
// blank screen at somebody who is mid-word.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { IsoDay, Note, NoteSection, NotesResponse } from '../api/client.ts';
import type { Saved } from './note-model.ts';

/**
 * How long typing stops before the search is sent.
 *
 * Long enough that an ordinary word is one request rather than six, short
 * enough that the list has caught up by the time the eye has. The list is not
 * cleared in between, so this delay is never a blank screen.
 */
export const SEARCH_DELAY_MS = 220;

/** Where a row that was just written ended up, as far as this screen goes. */
type Landing = 'listed' | 'hidden-by-search' | 'not-listed';

/** Which note the editor is on, or null when the list is what is on screen. */
export type Editing =
  /** A note nobody has written yet: the editor holds a draft and no row. */
  { kind: 'new' } | { kind: 'open'; note: Note };

export interface RoutineNoteClear {
  changed: boolean;
  previousBody: string | null;
  restore: () => Promise<void>;
  retire: () => void;
}

export interface NotesHost {
  say: (message: string, undo?: () => void, retire?: () => void) => void;
  amendNotice: (undo: () => void, message: string) => boolean;
  /** Takes the toast away, so an Undo cannot be pressed twice. */
  dismiss: (undo?: () => void) => void;
  /** Any 401, from this screen's own read or from any write it makes. */
  signOut: () => void;
  shareRevision: number;
  pendingShareStatus: ReadonlyMap<string, boolean>;
  onShareStatusRead: (revision: number, observed: ReadonlyMap<string, boolean>) => void;
  /** Changes made outside this screen, including a reminder-note clear or Undo. */
  routineNoteRevision: number;
  onRoutineNoteChanged: () => void;
}

export interface NotesState {
  /** The user's today, from the payload. Null until the first read lands. */
  today: IsoDay | null;
  /**
   * Null until the first read lands; kept while a later one is in flight.
   *
   * There is deliberately no `loading` beside it. Keeping the last list up while
   * the next answer travels is the whole of this screen's loading behaviour, so
   * "a read is in flight" has nothing left to draw - and a flag nothing reads is
   * a re-render per read and a question about which of the two to trust.
   */
  notes: Note[] | null;
  deleted: Note[];
  /**
   * The account's live note groups, in the order the server sent them - pinned
   * first, then by name. Walked, never sorted here: the order is a product rule
   * and it lives on the server.
   */
  sections: NoteSection[];
  /** The archived ones. They still hold their notes, and restoring one is one PATCH. */
  archivedSections: NoteSection[];
  problem: string | null;
  /** What is in the box, which is what its own Clear button empties. */
  search: string;
  /**
   * The term the list on screen was actually READ with - the last one that came
   * BACK, not the one a read is out for.
   *
   * Both travel because they answer different questions. Anything describing the
   * LIST has to ask this one: for a debounce plus a round trip after every
   * keystroke the box says something the list has not been asked yet, and a
   * sentence about the list drawn from the box describes a screen that is not
   * there. The debounced term is no better - it moves the moment the pause is
   * over, so a read that FAILS would leave it naming a search whose answer never
   * arrived while the list underneath is still the previous one, which this
   * screen keeps on purpose.
   */
  listedFor: string;
  setSearch: (value: string) => void;
  editing: Editing | null;
  open: (note: Note) => void;
  openNew: () => void;
  /**
   * Leave the editor. `saved` is what the last write left on the server, or
   * null when nothing was written - an editor opened and closed again writes
   * nothing and says nothing.
   */
  close: (saved: Saved | null) => void;
  routineNoteSaved: (noteId: string) => void;
  routineNoteSaveFailed: (noteId: string, message: string) => void;
  routineNoteCleared: (
    note: Note,
    restoreBody: (() => Promise<void>) | null,
    retireEditorClear: () => void,
  ) => void;
  refresh: () => void;
  restore: (note: Note) => Promise<void>;
  deleteForever: (note: Note) => Promise<void>;
  /**
   * Delete a note of its own, or take the note off an item - with the Undo that
   * every destructive action in this product owes (AGENTS.md).
   *
   * Answers whether the note WENT - true when the write landed and the editor
   * was taken off screen, false when it failed and all that happened was the
   * toast. The editor orders an item clear after any save already sent and
   * pauses later saves while it asks, so it has to be told when the clear did
   * not land and the open editor may resume.
   */
  remove: (note: Note, clearRoutineNote?: () => Promise<RoutineNoteClear>) => Promise<boolean>;

  // ------------------------------------------------------- groups and pins --
  //
  // Every one of these ends in a `refresh()` and a sentence, exactly as the
  // writes above do. Which of them offer an Undo is the product rule rather than
  // a preference: the destructive ones must (AGENTS.md), and a MOVE does too,
  // because a drag is the one control on this screen that can be finished
  // somewhere nobody aimed at.

  /** Hold a note at the top of the screen, or let it go. */
  setPinned: (note: Note, pinned: boolean) => Promise<void>;
  /**
   * File a note in a group, or take it out of one with `null`. Undoable.
   *
   * `unpin` sends the pin off in the SAME patch, and is for the one caller that
   * needs it: a note dragged out of the pinned area onto a group has to end up
   * in the group and unpinned, or it springs back to the top. Two requests were
   * the first shape and are wrong - see the note at the implementation.
   */
  moveNote: (note: Note, sectionId: string | null, unpin?: boolean) => Promise<void>;
  /** Make a group. Answers whether it was made, so a form can clear itself. */
  createSection: (name: string) => Promise<boolean>;
  renameSection: (section: NoteSection, name: string) => Promise<void>;
  pinSection: (section: NoteSection, pinned: boolean) => Promise<void>;
  /**
   * Archive a group. THE NOTES IN IT ARE NOT TOUCHED - the toast says so, and
   * the Undo puts the group back with every note still in it.
   */
  removeSection: (section: NoteSection) => Promise<void>;
  restoreSection: (section: NoteSection) => Promise<void>;
  /** Gone. Its notes are not, which is what the sentence says. No undo. */
  deleteSectionForever: (section: NoteSection) => Promise<void>;
}

function applyPendingShareStatus(notes: Note[], pending: ReadonlyMap<string, boolean>): Note[];
function applyPendingShareStatus(
  notes: Note[] | null,
  pending: ReadonlyMap<string, boolean>,
): Note[] | null;
function applyPendingShareStatus(
  notes: Note[] | null,
  pending: ReadonlyMap<string, boolean>,
): Note[] | null {
  if (notes === null || pending.size === 0) return notes;
  return notes.map((note) => {
    const shared = note.kind === 'note' ? pending.get(note.id) : undefined;
    return shared === undefined || shared === note.shared ? note : { ...note, shared };
  });
}

export function useNotes(host: NotesHost): NotesState {
  const {
    say,
    amendNotice,
    dismiss,
    signOut,
    shareRevision,
    pendingShareStatus,
    onShareStatusRead,
    routineNoteRevision,
    onRoutineNoteChanged,
  } = host;

  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [today, setToday] = useState<IsoDay | null>(null);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [listedFor, setListedFor] = useState('');
  const [deleted, setDeleted] = useState<Note[]>([]);
  const [sections, setSections] = useState<NoteSection[]>([]);
  const [archivedSections, setArchivedSections] = useState<NoteSection[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState<Editing | null>(null);
  const routineClearUndo = useRef<{
    noteId: string;
    message: string;
    undo?: () => void;
  } | null>(null);

  // Read through a ref, so the read effect below depends on WHICH read is
  // wanted rather than on how this screen's caller happens to be rendered -
  // `signOut` is a fresh closure every render and depending on it would re-read
  // the whole list on every keystroke anywhere in the shell.
  const latest = useRef(signOut);
  latest.current = signOut;

  useEffect(() => {
    if (applied === search) return;
    const timer = setTimeout(() => setApplied(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, applied]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const body = await api.notes(applied);
        // An answer from a search that has since been typed past is dropped
        // here, which is what keeps the list from flickering back to an older
        // set of results when two requests land out of order.
        if (!live) return;
        setToday(body.today);
        setNotes(body.notes);
        // Moved WITH the list, never before it: whatever is drawn about the list
        // has to be true of the one on screen, and a term that ran ahead of its
        // answer would describe a list nobody has been shown.
        setListedFor(applied);
        setDeleted(body.deleted);
        // Moved with the list for the same reason `listedFor` is: a heading is
        // drawn over rows, so the groups and the rows they hold have to be the
        // answer to one read. The groups themselves are NOT filtered by the
        // search - the server sends them all - so this is not a second set that
        // can disagree about what matched.
        //
        // `?? []` where the day screen instead refuses to draw a day whose
        // neighbours are missing, and the difference is what defaulting COSTS.
        // Falling back to today's date there draws the wrong day and writes a
        // tick against it; falling back to no groups here draws the list this
        // screen has always drawn, with every note on it and nothing lost but
        // the headings. A white screen would be the worse of the two.
        setSections(body.sections ?? []);
        setArchivedSections(body.archivedSections ?? []);
        setProblem(null);
        const observedShareStatus = new Map<string, boolean>();
        for (const note of [...body.notes, ...body.deleted]) {
          if (note.kind === 'note') observedShareStatus.set(note.id, note.shared);
        }
        onShareStatusRead(shareRevision, observedShareStatus);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current();
          return;
        }
        // The list already on screen is kept: it may still be right, and
        // replacing it with a sentence loses whatever was readable.
        if (live) setProblem(messageFor(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [applied, revision, shareRevision, routineNoteRevision, onShareStatusRead]);

  const shownNotes = useMemo(
    () => applyPendingShareStatus(notes, pendingShareStatus),
    [notes, pendingShareStatus],
  );
  const shownDeleted = useMemo(
    () => applyPendingShareStatus(deleted, pendingShareStatus),
    [deleted, pendingShareStatus],
  );

  const refresh = useCallback(() => {
    setRevision((n) => n + 1);
  }, []);

  /**
   * Where the row that was just saved actually LANDED, in the server's own
   * words, which is three answers and not two:
   *
   *   listed            it is on the list this screen is about to draw;
   *   hidden-by-search  it is on the account, but not under the term in the box;
   *   not-listed        it is not on the list at all.
   *
   * Asked of the SERVER rather than worked out here, exactly as
   * `useEditor.notShownOn` asks the day whether it draws a row: matching is one
   * rule and it lives in one place. A second copy of it in this client would be
   * a second answer, and the one it disagreed with would be the one the person
   * is looking at.
   *
   * ONE read where the box is empty, which is the common path, and a second only
   * where there is a term and the row was not under it - which is the only case
   * where "is the search why" is still an open question.
   */
  const whereItLanded = useCallback(
    async (id: string): Promise<Landing> => {
      const holds = (body: NotesResponse) =>
        body.notes.some((n) => n.id === id) || body.deleted.some((n) => n.id === id);
      try {
        if (holds(await api.notes(search))) return 'listed';
        if (search.trim() === '') return 'not-listed';
        return holds(await api.notes('')) ? 'hidden-by-search' : 'not-listed';
      } catch {
        // The write landed; only the check did not. The plain sentence is still
        // true, and the read this screen is doing anyway reports its own
        // failure - this must not invent a second one.
        return 'listed';
      }
    },
    [search],
  );

  const openAttempt = useRef(0);
  const open = useCallback(
    (note: Note) => {
      const attempt = ++openAttempt.current;
      if (note.kind === 'note') {
        setEditing({ kind: 'open', note });
        return;
      }

      void (async () => {
        try {
          const routine = await api.routine(note.id);
          if (openAttempt.current !== attempt) return;
          if (routine.note === '') {
            refresh();
            return;
          }
          setEditing({
            kind: 'open',
            note: {
              ...note,
              title: routine.title,
              body: routine.note,
              routineRevision: routine.revision,
            },
          });
        } catch (error) {
          if (isSignedOut(error)) {
            signOut();
            return;
          }
          if (openAttempt.current === attempt) {
            say(`"${note.heading}" could not be opened. ${messageFor(error)}`);
          }
        }
      })();
    },
    [refresh, say, signOut],
  );

  const openNew = useCallback(() => {
    openAttempt.current += 1;
    setEditing({ kind: 'new' });
  }, []);

  /**
   * The Undo on a toast, run with no sheet on screen - so its own failure has
   * nowhere to go but the toast, and it has to be said: an undo that quietly
   * does nothing is worse than no undo at all.
   *
   * The line goes first, before the request. A button left up while the write
   * behind it is in flight is a button that can be pressed twice, and the second
   * press arrives at a note already restored and is reported as a failure for
   * the undo that worked (`undoWith` in edit/useEditor.ts, same reasoning).
   */
  const undoWith = useCallback(
    (work: () => Promise<unknown>, after?: () => void) => () => {
      dismiss();
      void (async () => {
        try {
          await work();
          refresh();
          after?.();
        } catch (error) {
          if (isSignedOut(error)) {
            signOut();
            return;
          }
          say(`That could not be undone. ${messageFor(error)}`);
        }
      })();
    },
    [dismiss, refresh, say, signOut],
  );

  const routineNoteSaved = useCallback(
    (noteId: string) => {
      const pending = routineClearUndo.current;
      if (pending?.noteId !== noteId) return;
      routineClearUndo.current = null;
      dismiss(pending.undo);
    },
    [dismiss],
  );

  const routineNoteSaveFailed = useCallback(
    (noteId: string, message: string) => {
      const pending = routineClearUndo.current;
      if (pending?.noteId === noteId) {
        const combined = `${pending.message} ${message}`;
        if (pending.undo !== undefined && amendNotice(pending.undo, combined)) return;
        if (pending.undo === undefined) {
          const next = { ...pending, message: combined };
          say(combined, undefined, () => {
            if (routineClearUndo.current === next) routineClearUndo.current = null;
          });
          routineClearUndo.current = next;
          return;
        }
        routineClearUndo.current = null;
      }
      say(message);
    },
    [amendNotice, say],
  );

  /**
   * THE NOTE CAME OFF AN ITEM - said in one place, because it happens two ways.
   *
   * Pressing the bin does it, and so does selecting the text and deleting it: an
   * item is on this list only while its note has something in it
   * (`canonical_routine_note(r.note) <> ''`, api/src/db/repositories/notes.ts), so emptying one
   * takes the row off this screen exactly as the bin does. Those are THE SAME
   * ACT by different routes, and it is indefensible that one is undoable and the
   * other silently is not - so they get the same sentence and the same Undo,
   * from here.
   *
   * Two other answers were considered and rejected: saying it in its own words
   * with no Undo leaves somebody's writing gone with a polite explanation, which
   * is not the bar this screen sets for itself; and treating an emptied note as
   * the delete outright is worse still, because silently turning typing into a
   * delete is the kind of surprise that makes people distrust an app for good.
   *
   * The restore callback carries what the SERVER held before, which is what an undo has to put back.
   */
  const routineNoteCleared = useCallback(
    (note: Note, restoreBody: (() => Promise<void>) | null, retireEditorClear: () => void) => {
      const message = `The note on "${note.heading}" was removed. The item itself is untouched.`;
      if (restoreBody === null) {
        const pending = { noteId: note.id, message };
        say(message, undefined, () => {
          if (routineClearUndo.current === pending) routineClearUndo.current = null;
          retireEditorClear();
        });
        routineClearUndo.current = pending;
        return;
      }
      const restore = undoWith(restoreBody, () => {
        setEditing((current) =>
          current?.kind === 'open' && current.note.kind === 'item' && current.note.id === note.id
            ? null
            : current,
        );
        onRoutineNoteChanged();
      });
      let restoring = false;
      const undo = () => {
        if (routineClearUndo.current?.undo !== undo) return;
        routineClearUndo.current = null;
        restoring = true;
        restore();
      };
      const pending = { noteId: note.id, message, undo };
      const retire = () => {
        if (routineClearUndo.current === pending) routineClearUndo.current = null;
        if (!restoring) retireEditorClear();
      };
      say(message, undo, retire);
      routineClearUndo.current = pending;
    },
    [onRoutineNoteChanged, say, undoWith],
  );

  const close = useCallback(
    (saved: Saved | null) => {
      setEditing(null);
      if (saved === null) return;
      refresh();
      void (async () => {
        if (routineClearUndo.current?.noteId === saved.id) return;
        const landing = await whereItLanded(saved.id);
        const opened = saved.opened;

        if (landing === 'not-listed' && opened !== null && opened.kind === 'item') return;

        // Named, because every successful write in this client names what it
        // acted on - and told where it went when it can land off the screen it
        // came back to, which a search is the notes screen's own version of
        // (web/src/README.md). It names no other search and offers no button:
        // the box is right there with the words still in it.
        say(
          landing === 'hidden-by-search'
            ? `"${saved.heading}" saved. It does not match what you have searched for.`
            : `"${saved.heading}" saved.`,
        );
      })();
    },
    [refresh, say, whereItLanded],
  );

  const remove = useCallback(
    async (note: Note, clearRoutineNote?: () => Promise<RoutineNoteClear>): Promise<boolean> => {
      let cleared: RoutineNoteClear | null = null;
      try {
        if (note.kind === 'item') {
          if (clearRoutineNote === undefined) return false;
          cleared = await clearRoutineNote();
          if (!cleared.changed) return false;
          onRoutineNoteChanged();
        } else {
          await api.deleteNote(note.id);
        }
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return false;
        }
        say(`"${note.heading}" was not deleted. ${messageFor(error)}`);
        return false;
      }

      setEditing(null);
      refresh();
      if (note.kind === 'item') {
        if (cleared === null || cleared.previousBody === null || cleared.previousBody === '') {
          if (cleared !== null) routineNoteCleared(note, null, cleared.retire);
        } else if (routineClearUndo.current?.noteId !== note.id) {
          routineNoteCleared(note, cleared.restore, cleared.retire);
        }
      } else {
        say(
          `"${note.heading}" deleted.`,
          undoWith(() => api.updateNote(note.id, { deleted: false })),
        );
      }
      return true;
    },
    [onRoutineNoteChanged, refresh, routineNoteCleared, routineNoteSaved, say, signOut, undoWith],
  );

  const restore = useCallback(
    async (note: Note) => {
      try {
        await api.updateNote(note.id, { deleted: false });
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return;
        }
        say(`"${note.heading}" was not put back. ${messageFor(error)}`);
        return;
      }
      refresh();
      say(
        `"${note.heading}" is back.`,
        undoWith(() => api.deleteNote(note.id)),
      );
    },
    [refresh, say, signOut, undoWith],
  );

  const deleteForever = useCallback(
    async (note: Note) => {
      try {
        await api.deleteNoteForever(note.id);
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return;
        }
        say(`"${note.heading}" was not deleted. ${messageFor(error)}`);
        return;
      }
      refresh();
      // No Undo, and none offered: this is the one act the API cannot take
      // back, which is why the button asked first.
      say(`"${note.heading}" deleted for good.`);
    },
    [refresh, say, signOut],
  );

  // -------------------------------------------------------- groups and pins --

  /**
   * One write, said out loud whichever way it goes.
   *
   * Written once because every action below has the same three outcomes and the
   * same three answers: a 401 raises the gate and says nothing (the shell is
   * about to be replaced), a failure names what did NOT happen and why, and a
   * success re-reads and says what did. Six copies of that would be six chances
   * to swallow one.
   *
   * `refused` is the sentence for the failure, ALREADY NAMING the thing acted on
   * - a toast that said only "That did not work" is a toast about nothing.
   */
  const write = useCallback(
    async (
      work: () => Promise<unknown>,
      refused: string,
      done?: { message: string; undo?: () => Promise<unknown> },
    ): Promise<boolean> => {
      try {
        await work();
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return false;
        }
        say(`${refused} ${messageFor(error)}`);
        return false;
      }
      refresh();
      if (done !== undefined) {
        const undo = done.undo;
        say(done.message, undo === undefined ? undefined : undoWith(undo));
      }
      return true;
    },
    [refresh, say, signOut, undoWith],
  );

  const setPinned = useCallback(
    async (note: Note, pinned: boolean) => {
      await write(
        () => api.updateNote(note.id, { pinned }),
        `"${note.heading}" was not ${pinned ? 'pinned' : 'unpinned'}.`,
        // No Undo: the control that did this is a toggle and is still under the
        // cursor, so the way back is the button that was just pressed. The
        // destructive-action rule is about acts that lose something, and this
        // loses nothing.
        { message: pinned ? `"${note.heading}" pinned.` : `"${note.heading}" unpinned.` },
      );
    },
    [write],
  );

  const moveNote = useCallback(
    async (note: Note, sectionId: string | null, unpin = false) => {
      const to = sections.find((section) => section.id === sectionId) ?? null;
      const from = sections.find((section) => section.id === note.noteSectionId) ?? null;
      const alsoUnpin = unpin && note.pinned;
      const stayed = (note.noteSectionId ?? null) === (sectionId ?? null);
      // Nothing to do, and nothing to say. A drag that ends where it started
      // and a picker set to what it already showed are both this.
      if (stayed && !alsoUnpin) return;

      // EVERY TOAST ON THIS SCREEN NAMES THE CHANGE IT MADE, and that is the
      // rule the next sentence added here is measured against. A confirmation
      // reporting a state that did not move is worse than silence: it spends
      // somebody's attention and tells them nothing.
      //
      // Which is why the unpin has a branch of its own. Dragging a pinned note
      // that is in no section onto "Not in a section" is the natural
      // unpin-by-drag, and it lands with `to` and `from` both null - so the
      // sentence used to be "is not in a section", which was already true
      // before the drag and never named the one thing that changed. When the
      // section DID move the move is what was aimed at and the move is what is
      // said, for the reason the patch note below gives.
      const said = stayed
        ? `"${note.heading}" unpinned.`
        : to === null
          ? from === null
            ? `"${note.heading}" is not in a section.`
            : `"${note.heading}" taken out of ${from.name}.`
          : `"${note.heading}" moved to ${to.name}.`;

      // ONE PATCH, even when the move also unpins - which is what `unpin` is
      // for. Dragging a note OUT of the pinned area and onto a section has to
      // leave it in the section and no longer pinned, or it springs straight
      // back to the top and the drag looks as if it failed. Sending that as two
      // requests was the first shape and is wrong twice over: two toasts race,
      // so the one carrying the Undo can be the one that loses, and the pin's
      // own sentence would describe an act nobody performed - what was aimed at
      // is the move. So both fields travel together, and the undo puts both
      // back.
      const patch = alsoUnpin
        ? { noteSectionId: sectionId, pinned: false }
        : { noteSectionId: sectionId };

      await write(() => api.updateNote(note.id, patch), `"${note.heading}" was not moved.`, {
        message: said,
        // A DRAG CAN LAND SOMEWHERE NOBODY AIMED AT, which is the one thing
        // separating this from the pin above. The way back is read from the
        // ROW rather than from the screen, so it is what the server held
        // before this write and not whatever the next read happens to say.
        undo: () =>
          api.updateNote(
            note.id,
            alsoUnpin
              ? { noteSectionId: note.noteSectionId, pinned: note.pinned }
              : { noteSectionId: note.noteSectionId },
          ),
      });
    },
    [sections, write],
  );

  const createSection = useCallback(
    async (name: string): Promise<boolean> =>
      write(() => api.createNoteSection(name), `"${name}" was not added.`, {
        message: `"${name}" added. Drag a note onto it, or use the section picker on a note.`,
      }),
    [write],
  );

  const renameSection = useCallback(
    async (section: NoteSection, name: string) => {
      if (name === section.name) return;
      await write(
        () => api.updateNoteSection(section.id, { name }),
        `"${section.name}" was not renamed.`,
        { message: `"${section.name}" is now "${name}".` },
      );
    },
    [write],
  );

  const pinSection = useCallback(
    async (section: NoteSection, pinned: boolean) => {
      await write(
        () => api.updateNoteSection(section.id, { pinned }),
        `"${section.name}" was not ${pinned ? 'pinned' : 'unpinned'}.`,
        {
          message: pinned ? `"${section.name}" pinned to the top.` : `"${section.name}" unpinned.`,
        },
      );
    },
    [write],
  );

  const removeSection = useCallback(
    async (section: NoteSection) => {
      // THE PROMISE, AND DELIBERATELY NO NUMBER. "Deleting a section must never
      // silently take the notes in it" is a correctness requirement here, and
      // this sentence is the ONE PLACE it is made to a person - at the moment
      // they are deciding whether to press the button. A count there is worse
      // than no count, because the only count this client can reach is the list
      // ON SCREEN: search-filtered and with the soft-deleted left out. So it
      // says "The note in it is safe" over a section holding five, and "It had
      // nothing in it" over a section full of notes in the Deleted group -
      // reassurance that misleads, at the most sensitive moment there is. The
      // countless sentence is true under a search, true with deleted notes in
      // the section, and true when it really was empty, and it answers the
      // question the person actually has, which is where the notes WENT.
      await write(() => api.archiveNoteSection(section.id), `"${section.name}" was not deleted.`, {
        message: `"${section.name}" deleted. The notes in it are safe, and are now in no section.`,
        // Exact rather than approximate: nothing about the notes was rewritten,
        // so putting the group back puts every one of them back in it.
        undo: () => api.updateNoteSection(section.id, { archived: false }),
      });
    },
    [write],
  );

  const restoreSection = useCallback(
    async (section: NoteSection) => {
      await write(
        () => api.updateNoteSection(section.id, { archived: false }),
        `"${section.name}" was not put back.`,
        {
          message: `"${section.name}" is back.`,
          undo: () => api.archiveNoteSection(section.id),
        },
      );
    },
    [write],
  );

  const deleteSectionForever = useCallback(
    async (section: NoteSection) => {
      await write(
        () => api.deleteNoteSectionForever(section.id),
        `"${section.name}" was not deleted.`,
        // No Undo, and none offered: this is the one act the API cannot take
        // back, which is why the button asked first. The second sentence is the
        // whole reason it is safe to offer at all.
        {
          message: `"${section.name}" deleted for good. The notes that were in it are still here.`,
        },
      );
    },
    [write],
  );

  return {
    today,
    notes: shownNotes,
    deleted: shownDeleted,
    sections,
    archivedSections,
    problem,
    search,
    listedFor,
    setSearch,
    editing,
    open,
    openNew,
    close,
    routineNoteSaved,
    routineNoteSaveFailed,
    routineNoteCleared,
    refresh,
    restore,
    deleteForever,
    remove,
    setPinned,
    moveNote,
    createSection,
    renameSection,
    pinSection,
    removeSection,
    restoreSection,
    deleteSectionForever,
  };
}
